import { inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import {
  SetupUser,
  DropdownData,
  LocalStorageManagerService,
  ApiCallingV2Service,
  API_CONFIG,
  AnalyticsEvent,
  AnalyticsService
} from '@zayan-erp/shared';
import { Prospect } from '../models/prospect.models';
import { CoBuyer, CreateCoBuyer, UpdateCoBuyer, ToggleCoBuyerPropertiesRequest } from '../models/co-buyer.models';
import { LinkedSalesAgentInfo } from '../models/linked-sales-agent.models';
import { LinkedRealtorAgentToLot } from '../models/linked-realtor-agent.models';
import { LinkedCoBuyerLotWithDetails } from '../models/linked-co-buyer.models';
import { HolidayCalendarSummary, HolidayCalendarSummaryParams } from '../models/holiday-calendar.models';
import { SpecLot, SpecLotModelOption, SpecLotModelResponse } from '../models/build-spec-models.models';
import { ModelImport } from '@zayan-erp/sales';
import { HttpParams } from '@angular/common/http';
import { SalesApsService } from './sales-aps.service';
import { WelcomeEmailStatusDto } from '../models/email-templates.models';
@Injectable({
  providedIn: 'root'
})
export class SalesService {
  #http = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  #localStorageService = inject(LocalStorageManagerService);
  #analytics = inject(AnalyticsService);
  #salesAps = inject(SalesApsService);
  baseUrl = this.#apiConfig.baseUrlV2;

  private saleOfficeId = new BehaviorSubject<string | null>(null);
  saleOfficeId$ = this.saleOfficeId.asObservable();
  readonly refreshTrigger = signal(0);

  set setSaleOfficeId(value: string | null) {
    this.saleOfficeId.next(value);
    if (value) this.#localStorageService.setSalesOfficeIdToStorage(value);
  }

  triggerSpecLotsRefresh(): void {
    this.refreshTrigger.update(v => v + 1);
  }

  readSalesUsers(): Observable<SetupUser[]> {
    return this.#http.getData(`${this.baseUrl}/sales/users`, { showLoader: true, showMessage: false }).pipe(
      map(users =>
        users.map((user: any) => ({
          ...user,
          lastName: user.lastname
        }))
      )
    );
  }

  readSalesUsersBySalesOffice(salesOfficeId: string): Observable<SetupUser[]> {
    return this.#http
      .getData(`${this.baseUrl}/SalesOffice/${salesOfficeId}/users`, { showLoader: false, showMessage: false })
      .pipe(
        map(users =>
          users.map((user: any) => ({
            ...user,
            lastName: user.lastname
          }))
        )
      );
  }

  updateSalesUser(obj: any, id: string): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/sales/users/${id}`,
      { showLoader: true, showMessage: true, message: 'Sales user updated successfully' },
      obj
    );
  }

  createProspect(prospect: any): Observable<Prospect> {
    return this.#http.postData(
      `${this.baseUrl}/Prospect`,
      { showLoader: true, showMessage: true, message: 'Prospect created successfully' },
      prospect
    );
  }

  createProspectPublic(prospect: any, recaptchaToken: string): Observable<Prospect> {
    prospect.captchaToken = recaptchaToken;

    return this.#http.postData(
      `${this.baseUrl}/public/prospect/register`,
      { showLoader: true, showMessage: true, message: 'Prospect created successfully' },
      prospect
    );
  }

  updateProspect(id: string, prospect: Prospect): Observable<Prospect> {
    return this.#http.putData(`${this.baseUrl}/Prospect`, prospect, {
      showLoader: true,
      showMessage: true,
      message: 'Prospect updated successfully'
    });
  }

  readProspects(
    page: number = 1,
    pageSize: number = 20,
    salesOfficeId: string | null,
    search: string
  ): Observable<{ totalCount: number; items: Prospect[] }> {
    let params = new HttpParams({
      fromObject: {
        pageNumber: page.toString(),
        pageSize: pageSize.toString(),
        search
      }
    });

    if (salesOfficeId) {
      params = params.set('salesOfficeId', salesOfficeId);
    }

    return this.#http.getData(`${this.baseUrl}/Prospect`, {
      showLoader: true,
      showMessage: false,
      options: { params }
    });
  }

  readProspectById(id: string): Observable<Prospect> {
    return this.#http.getData(`${this.baseUrl}/Prospect/${id}`, { showLoader: true, showMessage: false });
  }
  deleteProspect(id: string): Observable<unknown> {
    return this.#http.deleteData(`${this.baseUrl}/Prospect/${id}`, { showLoader: true, showMessage: true });
  }

  validateProspect(email: string, salesOfficeId: string): Observable<any> {
    const params = new URLSearchParams({
      email,
      salesOfficeId
    });
    return this.#http.getData(`${this.baseUrl}/Prospect/validate?${params.toString()}`, {
      showLoader: true,
      showMessage: false
    });
  }

  scanIdentityCard(frontImageUrl: string, backImageUrl?: string): Observable<any> {
    const requestBody = {
      frontImageUrl,
      backImageUrl
    };

    return this.#http.postData(
      `${this.baseUrl}/Prospect/scan-identity-card`,
      { showLoader: true, showMessage: true, message: 'Identity card scanned successfully' },
      requestBody
    );
  }

  createCoBuyer(coBuyer: CreateCoBuyer): Observable<CoBuyer> {
    return this.#http.postData(
      `${this.baseUrl}/co-buyer`,
      { showLoader: true, showMessage: true, message: 'Co-Buyer created successfully' },
      coBuyer
    );
  }

  updateCoBuyer(id: string, coBuyer: UpdateCoBuyer): Observable<CoBuyer> {
    return this.#http.putData(`${this.baseUrl}/co-buyer/${id}`, coBuyer, {
      showLoader: true,
      showMessage: true,
      message: 'Co-Buyer updated successfully'
    });
  }

  readCoBuyerById(id: string): Observable<CoBuyer> {
    return this.#http.getData(`${this.baseUrl}/co-buyer/${id}`, { showLoader: true, showMessage: false });
  }

  readCoBuyers(prospectId: string, includeInactive: boolean): Observable<CoBuyer[]> {
    const endpoint = `prospect/${prospectId}?includeInactive=${includeInactive}`;

    return this.#http.getData(`${this.baseUrl}/co-buyer/${endpoint}`, { showLoader: true, showMessage: false });
  }

  toggleCoBuyerStatus(id: string): Observable<CoBuyer> {
    return this.#http.patchData(`${this.baseUrl}/co-buyer/${id}/toggle`, null, {
      showLoader: true,
      showMessage: true,
      message: 'Co-Buyer status toggled successfully'
    });
  }

  linkCoBuyerToLot(coBuyerId: string, dealId: string): Observable<void> {
    return this.#http.postData(
      `${this.baseUrl}/co-buyer/lot`,
      { showLoader: true, showMessage: true, message: 'Co-Buyer linked successfully' },
      { coBuyerId, dealId }
    );
  }

  readLinkedCoBuyersByLot(): Observable<LinkedCoBuyerLotWithDetails[]> {
    return this.#http.getData(`${this.baseUrl}/co-buyer/lot`, { showLoader: true, showMessage: false });
  }

  readLinkedCoBuyersByDeadId(
    dealId: string,
    includeInactive: boolean = false
  ): Observable<LinkedCoBuyerLotWithDetails[]> {
    const endpoint = `deal/${dealId}?includeInactive=${includeInactive}`;
    return this.#http.getData(`${this.baseUrl}/co-buyer/${endpoint}`, { showLoader: true, showMessage: false });
  }

  unlinkCoBuyerFromLot(lotCoBuyerId: string): Observable<void> {
    return this.#http.patchData(`${this.baseUrl}/co-buyer/lot/${lotCoBuyerId}/toggle`, null, {
      showLoader: true,
      showMessage: true,
      message: 'Co-Buyer unlinked successfully'
    });
  }
  updateCoBuyerLotProperties(lotCoBuyerId: string, properties: ToggleCoBuyerPropertiesRequest): Observable<void> {
    return this.#http.patchData(`${this.baseUrl}/co-buyer/lot/${lotCoBuyerId}/properties/toggle`, properties, {
      showLoader: true,
      showMessage: true,
      message: 'Co-Buyer properties updated successfully'
    });
  }
  updateCoBuyerMailingListStatus(lotCoBuyerId: string, addToMailingList: boolean): Observable<void> {
    return this.updateCoBuyerLotProperties(lotCoBuyerId, { toggleAddToMailingList: addToMailingList });
  }
  updateCoBuyerContractStatus(lotCoBuyerId: string, includeInContract: boolean): Observable<void> {
    return this.updateCoBuyerLotProperties(lotCoBuyerId, { toggleIncludeInContract: includeInContract });
  }

  readSalesUserById(id: string): Observable<SetupUser> {
    return this.#http.getData(`${this.baseUrl}/sales/users/${id}`, { showLoader: true, showMessage: false });
  }

  linkSalesAgentToLot(linkedSalesAgentInfo: LinkedSalesAgentInfo, dealId: string): Observable<void> {
    return this.#http.postData(
      `${this.baseUrl}/deals/${dealId}/sales-agents`,
      { showLoader: true, showMessage: true, message: 'Sales Agent linked successfully' },
      linkedSalesAgentInfo
    );
  }

  readLinkedSalesAgentsByLot(dealId: string, showLoader = true): Observable<LinkedSalesAgentInfo[]> {
    return this.#http.getData(`${this.baseUrl}/deals/${dealId}/sales-agents`, { showLoader, showMessage: false });
  }

  unlinkSalesAgentFromLot(dealId: string, salesAgentId: string): Observable<void> {
    return this.#http.deleteData(`${this.baseUrl}/deals/${dealId}/sales-agents/${salesAgentId}`, {
      showLoader: true,
      showMessage: true,
      message: 'Sales Agent unlinked successfully'
    });
  }

  updateLinkedSalesAgent(dealId: string, salesAgentId: string, salesAgent: LinkedSalesAgentInfo): Observable<unknown> {
    return this.#http.putData(`${this.baseUrl}/deals/${dealId}/sales-agents/${salesAgentId}`, salesAgent, {
      showLoader: true,
      buttonId: 'save-sales-agent',
      showMessage: true,
      message: 'Sales Agent updated successfully'
    });
  }

  getLinkedSalesAgent(dealId: string, salesAgentId: string): Observable<LinkedSalesAgentInfo> {
    return this.#http.getData(`${this.baseUrl}/deals/${dealId}/sales-agents/${salesAgentId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  linkRealtorAgentToLot(linkedRealtorAgentInfo: LinkedRealtorAgentToLot, dealId: string): Observable<void> {
    return this.#http.postData(
      `${this.baseUrl}/deals/${dealId}/realtor-agents`,
      { showLoader: true, showMessage: true, message: 'Realtor Agent linked successfully' },
      linkedRealtorAgentInfo
    );
  }

  readLinkedRealtorAgentsByLot(dealId: string): Observable<LinkedRealtorAgentToLot[]> {
    return this.#http.getData(`${this.baseUrl}/deals/${dealId}/realtor-agents`, {
      showLoader: true,
      showMessage: false
    });
  }
  unlinkRealtorAgentFromLot(dealId: string, reAgentId: string): Observable<void> {
    return this.#http.deleteData(`${this.baseUrl}/deals/${dealId}/realtor-agents/${reAgentId}`, {
      showLoader: true,
      showMessage: true,
      message: 'Realtor Agent unlinked successfully'
    });
  }

  getLinkedRealtorAgent(dealId: string, realtorAgentId: string): Observable<LinkedRealtorAgentToLot> {
    return this.#http.getData(`${this.baseUrl}/deals/${dealId}/realtor-agents/${realtorAgentId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  updateLinkedRealtorAgent(
    dealId: string,
    realtorAgentId: string,
    realtorAgent: LinkedRealtorAgentToLot
  ): Observable<unknown> {
    return this.#http.putData(`${this.baseUrl}/deals/${dealId}/realtor-agents/${realtorAgentId}`, realtorAgent, {
      showLoader: true,
      buttonId: 'save-realtor-agent',
      showMessage: true,
      message: 'Realtor Agent updated successfully'
    });
  }
  getHolidayCalendarSummary(params: HolidayCalendarSummaryParams): Observable<HolidayCalendarSummary> {
    return this.#http.postData(
      `${this.baseUrl}/holiday-calendar/lot-closing-schedule`,
      { showLoader: true, showMessage: false, message: 'Holiday Calendar summary created successfully' },
      params
    );
  }

  readSpecLots(projectId: string): Observable<SpecLot[]> {
    return this.#http.getData(`${this.baseUrl}/Lot/spec-lots?projectId=${projectId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  readSpecLotDetails(lotId: string): Observable<SpecLot> {
    return this.#http.getData(`${this.baseUrl}/Lot/spec-lots/${lotId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  toggleReleaseToConstruction(lotId: string, lotNo?: string): Observable<unknown> {
    return this.#http.putData(`${this.baseUrl}/Lot/${lotId}/release-to-construction/toggle`, null, {
      showLoader: true,
      showMessage: !lotNo,
      message: 'Release to construction updated successfully',
      showError: true,
      errorMessage: lotNo ? `Failed to update lot ${lotNo}` : undefined
    });
  }

  readSpecLotModelElevations(projectId: string): Observable<SpecLotModelOption[]> {
    return this.#http
      .getData<SpecLotModelOption[] | null>(`${this.baseUrl}/Lot/spec-lots/model-elevations?projectId=${projectId}`, {
        showLoader: false,
        showMessage: false
      })
      .pipe(map(models => models ?? []));
  }

  readModelsBySpecLot(specLotId: string): Observable<DropdownData[]> {
    return this.#http
      .getData(`${this.baseUrl}/Lot/${specLotId}/models`, { showLoader: true, showMessage: false, showError: false })
      .pipe(
        map((res: SpecLotModelResponse[]) => {
          return res.map((item: SpecLotModelResponse) => ({
            id: item.modelId,
            firstLabel: item.modelName,
            secondLabel: item.projectName ?? ''
          })) as DropdownData[];
        })
      );
  }

  readModelsBySpecLotDetails(specLotId: string): Observable<SpecLotModelResponse[]> {
    return this.#http.getData(`${this.baseUrl}/Lot/${specLotId}/models`, {
      showLoader: true,
      showMessage: false,
      showError: false
    });
  }

  updateSpecLot(
    lotId: string,
    payload: { lotId: string; modelId: string | null; elevationId: string | null; lotTypeId?: string | null },
    lotNo?: string
  ): Observable<unknown> {
    return this.#http.putData(`${this.baseUrl}/Lot/spec-lots/${lotId}`, payload, {
      showLoader: true,
      showMessage: !lotNo,
      message: 'Spec lot updated successfully',
      showError: true,
      errorMessage: lotNo ? `Failed to update lot ${lotNo}` : undefined
    });
  }
  reassignBuyer(dealId: string, prospectId: string): Observable<unknown> {
    return this.#http
      .patchData(
        `${this.baseUrl}/deals/${dealId}/prospect/${prospectId}`,
        {},
        {
          showLoader: true,
          buttonId: 'save-reassign-buyer',
          showMessage: true,
          message: 'Buyer reassigned successfully'
        }
      )
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.DealBuyerReassigned, {
            ...this.#salesAps.dealCtx(dealId),
            buyer_id: prospectId
          });
        })
      );
  }

  generatePDF(params: HolidayCalendarSummaryParams): Observable<void> {
    console.log('generatePDF', params);
    return this.#http.downloadReportFileWithBody(
      `${this.baseUrl}/holiday-calendar/lot-closing-schedule`,
      params,
      { showLoader: true, showMessage: true, message: 'PDF generated successfully' },
      `sales_closing_${+new Date()}.pdf`
    );
  }

  getYtdActualClosings(projectIds: string[]): Observable<any> {
    return this.#http.postData(
      `${this.baseUrl}/sales-report/ytd-actual-closings`,
      { showLoader: true, showMessage: false, message: 'YTD actual closings generated successfully' },
      { projectIds }
    );
  }
  getIsDealSigned(dealId: string, showLoader = true): Observable<{ isDealSigned: boolean }> {
    return this.#http.getData(`${this.baseUrl}/deal-documents/deal/${dealId}/is-signed`, {
      showLoader,
      showMessage: false
    });
  }
  markAsClosed(dealId: string, closingDate: string): Observable<void> {
    return this.#http
      .patchData(
        `${this.baseUrl}/deals/${dealId}/close?closingDate=${closingDate}`,
        {},
        { showLoader: true, showMessage: true, message: 'Deal closed successfully' }
      )
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.DealClosed, {
            ...this.#salesAps.dealCtx(dealId),
            closing_date: closingDate
          });
        })
      );
  }
  getWelcomeEmailStatus(dealId: string): Observable<WelcomeEmailStatusDto> {
    return this.#http.getData<WelcomeEmailStatusDto>(`${this.baseUrl}/deals/${dealId}/welcome-email-status`, {
      showLoader: true,
      showMessage: false
    });
  }

  readModelsByLot(lotId: string, showLoader = true): Observable<DropdownData[]> {
    return this.#http
      .getData<ModelImport[]>(`${this.baseUrl}/Lot/${lotId}/models`, { showLoader, showMessage: false })
      .pipe(
        map((res: unknown) => {
          return (res as ModelImport[]) ?? [];
        })
      );
  }
}
